import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import type { ProductItem, ProductWithRelatedData } from '../types';
import isComplexProduct from '../util/isComplexProduct';
import useLoginState from '../../core/hooks/useLoginState';
import getCurrentPrice from '../../pricing/util/getCurrentPrice';
import notEmpty from '../../core/util/notEmpty';
import useProductDetailsLink from './useProductDetailsLink';

const getPrice = (
    isLoggedIn: boolean,
    product: ProductWithRelatedData,
    activeSKU?: string
) => {
    return getCurrentPrice({
        customerPrices: [
            product.customerPrice,
            ...product.variants.map(variant => variant.customerPrice),
        ].filter(notEmpty),
        selectedSku: activeSKU,
        isLoggedIn,
    });
};

const useProductView = ({
    product,
    renderInstantly,
}: {
    product:
        | (Pick<ProductItem, 'id' | 'sku'> & {
              variants?: null;
          })
        | ProductWithRelatedData;
    renderInstantly: boolean;
}) => {
    const isLoggedIn = useLoginState();
    const { ref, inView } = useInView({
        initialInView: renderInstantly,
        triggerOnce: true,
        rootMargin: '0px 0px 500px 0px',
        skip: renderInstantly,
    });
    const isShow = renderInstantly || inView;

    const isSimpleProduct = !isComplexProduct(product);

    const [activeSKU, setActiveSKU] = useState<string | undefined>(
        isSimpleProduct ? product.sku : undefined
    );

    const productDetailsLink = useProductDetailsLink(
        product.id,
        product.variants ?? [],
        activeSKU
    );

    if (product.variants) {
        const result = getPrice(isLoggedIn, product, activeSKU);
        return {
            ...result,
            isShow,
            ref,
            activeSKU,
            setActiveSKU,
            isSimpleProduct,
            productDetailsLink,
        };
    }

    return {
        isShow,
        ref,
        activeSKU,
        setActiveSKU,
        isSimpleProduct,
        productDetailsLink,
        customerPrice: undefined,
        startingPrice: false,
    };
};

export default useProductView;
